import React from 'react';
import GroupForm from './GroupForm';
import GroupsImg from '../../../images/sharing-208x165.png';
import {
    Button,
    Typography,
    MenuItem,
    MenuList,
    Grid,
} from '@material-ui/core';
import Access from 'ui/Access';
import { MTable } from 'ui/mtable/MTable';
import EditButton from 'ui/EditButton';
import DetailsButton from 'ui/DetailsButton';
import StatusChip from 'ui/StatusChip';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import { groupBy } from 'utils/collections';
import Dashboard from 'ui/dashboard/Dashboard';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import DashboardBoxActions from 'ui/dashboard/BoxActions';

import { loadGroups, loadGroup, loadUsers } from '../Services';
import formatters from '../../../formatters';
import WhiteBack from '../../WhiteBack';
import { withCompanyContext } from '../CompanyContext';
import * as api from '../../../api';

const isEditableGroup = group => group.name !== 'Everyone';

const DELETE_GROUPS = 'deleteGroups';
const MANAGE_IN_PASSWORDBOSS = 'manageGroupsInPasswordBoss';
const ADD_GROUP = 'addGroup';
const EDIT_GROUP = 'editGroup';
const DETAILS_GROUP = 'detailsGroup';

class Groups extends React.Component {
    state = {
        selectedGroups: [],
        currentAction: null,
        actionResultName: null,
        groups: [],
        loading: true,
        currentGroup: null,
        users: [],
        loaded: false,
    };

    componentDidUpdate = (prevProps, prevState) => {
        const hadError =
            prevProps.formErrors !== this.props.formErrors &&
            this.props.formErrors == null;
        const withoutError =
            prevProps.formErrors === this.props.formErrors &&
            prevState.currentAction !== null;
        if (!this.state.currentGroup && (withoutError || hadError)) {
            this.setState({ currentAction: null });
        }
    };

    componentDidMount() {
        this.fetchGroups();
        this.fetchUsers();
    }

    fetchUsers() {
        if (this.props.usersAccess) {
            loadUsers(this.props.company.id).then(({ users }) => {
                this.setState({ users });
            });
        }
    }

    fetchGroups() {
        this.setState({ loading: true }, () =>
            loadGroups(this.props.company.id).then(groups =>
                this.setState({ groups, loading: false, loaded: true })
            )
        );
    }

    fetchGroup({ uuid }) {
        const id = this.props.company.id;
        loadGroup(id, uuid).then(currentGroup =>
            this.setState({ currentGroup })
        );
    }

    addGroup = groupData => {
        const id = this.props.company.id;
        groupData.organization = id;
        api.post(`/api/organization/${id}/groups`, groupData).then(() => {
            this.setState({ currentAction: null, currentGroup: null }, () => {
                this.props.onMessage('Group will be created.');
                this.fetchGroups(id);
            });
        });
    };

    updateGroup = groupData => {
        const id = this.props.company.id;
        groupData.organization = id;

        api.patch(`/api/organization/${id}/groups`, groupData).then(
            response => {
                this.setState(
                    { currentAction: null, currentGroup: null },
                    () => {
                        if (response.error) {
                            this.props.onMessage(
                                'Unable to update this group.'
                            );
                        } else {
                            this.props.onMessage('Group will be updated.');
                        }
                        this.fetchGroups(id);
                    }
                );
            }
        );
    };

    deleteGroups = selectedGroups => {
        const id = this.props.company.id;
        const data = selectedGroups.map(({ uuid }) => ({
            uuid,
        }));
        api.remove(`/api/organization/${id}/groups`, data).then(response => {
            if (response.error) {
                this.props.onMessage('Can not delete this group.');
            } else {
                this.props.onMessage('Group(s) will be deleted.');
            }
            this.setState({ currentAction: null }, () => {
                this.fetchGroups(id);
            });
        });
    };

    manageGroupsInPasswordBoss = selectedGroups => {
        const id = this.props.company.id;
        const data = selectedGroups.map(({ uuid }) => ({
            uuid,
            managed_by: 'password-boss',
        }));

        api.patch(`/api/organization/${id}/groups`, data).then(() => {
            this.setState({ currentAction: null }, () => {
                this.fetchGroups(id);
            });
        });
    };

    getAvailableActions = (selectedGroups, actions) => {
        const managedBy = Object.keys(groupBy(selectedGroups, 'managed'));
        let selectedStatuses = Object.keys(groupBy(selectedGroups, 'status'));
        selectedStatuses = selectedStatuses.concat(managedBy);
        selectedStatuses = new Set(selectedStatuses.sort());
        const availableActions = actions.filter(action => {
            const some = action.notAllowedGroupsStatuses.filter(status => {
                return selectedStatuses.has(status);
            });
            return some.length === 0;
        });
        return availableActions;
    };

    getActions = props => {
        return [
            {
                key: DELETE_GROUPS,
                name: 'Delete groups',
                action: this.handleAction(DELETE_GROUPS),
                notAllowedGroupsStatuses: ['unmanaged', 'pending_delete'],
            },
            {
                key: MANAGE_IN_PASSWORDBOSS,
                name: 'Manage in CyberFOX Password Manager',
                action: this.handleAction(MANAGE_IN_PASSWORDBOSS),
                notAllowedGroupsStatuses: ['managed'],
            },
        ];
    };

    getColumns = editable => {
        return [
            {
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                notFilter: true,
                export: false,
            },
            {
                field: 'name',
                title: 'Group',
                defaultSort: 'asc',
                render: rowData => <strong>{rowData.name}</strong>,
                cellStyle: {
                    width: '50%',
                },
            },
            {
                field: 'members',
                title: 'Members',
                filterType: 'select',
                cellStyle: {
                    width: '100px',
                },
            },
            {
                field: 'shares',
                title: 'Shares',
                filterType: 'select',
                cellStyle: {
                    minWidth: '100px',
                },
            },
            {
                field: 'last_changed',
                title: 'Last Changed',
                render: rowData => formatters.date(rowData.last_changed),
                filterType: 'select',
                cellStyle: {
                    minWidth: '170px',
                },
            },
            {
                field: 'created',
                title: 'Created',
                render: rowData => formatters.date(rowData.created),
                filterType: 'select',
                cellStyle: {
                    minWidth: '170px',
                },
            },
            {
                field: 'status',
                title: 'Status',
                render: rowData => {
                    if (rowData.status === 'active') {
                        return <StatusChip label="Active" variant="active" />;
                    } else if (rowData.status === 'pending_delete') {
                        return (
                            <StatusChip
                                label="Pending delete"
                                variant="disabled"
                            />
                        );
                    }
                },
                filterType: 'select',
                exportValue: val => {
                    return val === 'active' ? 'Active' : 'Pending delete';
                },
                cellStyle: {
                    width: '150px',
                },
            },
            {
                field: 'managed_by',
                title: 'Managed By',
                render: row => {
                    if (!row.managed) {
                        return (
                            <span style={{ color: 'red' }}>
                                {formatters.managedByLabel(row.managed_by)}
                            </span>
                        );
                    }
                    return (
                        <span>{formatters.managedByLabel(row.managed_by)}</span>
                    );
                },
                filterType: 'select',
                // Relabels the dropdown options; the option values stay raw so
                // filtering still matches the api's data.
                formatOptionLabel: formatters.managedByLabel,
                exportValue: val => formatters.managedByLabel(val),
                cellStyle: {
                    minWidth: '150px',
                },
            },
            {
                field: 'action',
                title: '',
                notFilter: true,
                export: false,
                render: rowData => {
                    return rowData.managed_by === 'PB' &&
                        rowData.status === 'active' &&
                        isEditableGroup(rowData) &&
                        editable ? (
                        <EditButton onClick={this.handleEdit(rowData)} />
                    ) : (
                        <DetailsButton onClick={this.handleDetails(rowData)} />
                    );
                },
            },
        ];
    };

    handleAction = currentAction => selectedGroups => {
        this.setState({
            currentAction,
            selectedGroups,
        });
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.setState({ currentAction: null, actionResultName }, () =>
            callback(this.state.selectedGroups, Boolean(actionResultName))
        );
    };

    handleDetails = group => () => {
        this.setState({ currentAction: DETAILS_GROUP }, () =>
            this.fetchGroup(group)
        );
    };

    handleEdit = group => () => {
        this.setState({ currentAction: EDIT_GROUP }, () =>
            this.fetchGroup(group)
        );
    };

    renderDialog = () => {
        const { currentAction } = this.state;
        const dialog = {
            [DELETE_GROUPS]: {
                title: 'Delete group',
                warning: 'Are you sure you want to delete:',
            },
            [MANAGE_IN_PASSWORDBOSS]: {
                title: 'Manage in CyberFOX Password Manager',
                subtitle1:
                    'These groups were previously managed by an external directory connector.',
                subtitle2:
                    'That connector was removed from your account. If you will be re-adding the connector do not make any changes now. If you will not be re-adding the connector you should convert the group(s) to be managed by CyberFOX Password Manager, which will allow you to manage them via this website.',

                cancelButtonName: 'Do nothing',
                okButtonName: 'Manage in CyberFOX Password Manager',
            },
        };

        const callbacks = {
            [DELETE_GROUPS]: { callback: this.deleteGroups },
            [MANAGE_IN_PASSWORDBOSS]: {
                callback: this.manageGroupsInPasswordBoss,
            },
        };

        if (currentAction === ADD_GROUP) {
            return (
                <GroupForm
                    open={true}
                    mode="new"
                    users={this.state.users}
                    groups={this.state.groups}
                    formErrors={this.props.formErrors}
                    onCancel={() => this.setState({ currentAction: null })}
                    onOk={groupData => this.addGroup(groupData)}
                />
            );
        }

        if (currentAction === DETAILS_GROUP && this.state.currentGroup) {
            return (
                <GroupForm
                    open={true}
                    mode="details"
                    users={this.state.users}
                    currentGroup={this.state.currentGroup}
                    groupInfo={this.state.currentGroup}
                    groups={this.state.groups}
                    formErrors={this.props.formErrors}
                    okButtonName="OK"
                    onOk={() =>
                        this.setState({
                            currentAction: null,
                            currentGroup: null,
                        })
                    }
                />
            );
        }

        if (currentAction === EDIT_GROUP && this.state.currentGroup) {
            return (
                <GroupForm
                    open={true}
                    mode="edit"
                    users={this.state.users}
                    currentGroup={this.state.currentGroup}
                    groupInfo={this.state.currentGroup}
                    groups={this.state.groups}
                    onCancel={() =>
                        this.setState({
                            currentAction: null,
                            currentGroup: null,
                        })
                    }
                    okButtonName="Save changes"
                    onOk={groupData => this.updateGroup(groupData)}
                    onDelete={() =>
                        this.handleAction(DELETE_GROUPS)([
                            this.state.currentGroup,
                        ])
                    }
                />
            );
        }

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedGroups.map(item => item.name)}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    handleAddGroup = () => {
        this.setState({ currentAction: ADD_GROUP });
    };

    getAddGroupAction = editable => dropdownCloseCallback => {
        if (!editable) {
            return null;
        }
        return (
            <MenuList>
                <MenuItem
                    onClick={() => {
                        this.handleAddGroup();
                        dropdownCloseCallback();
                    }}
                >
                    Add group
                </MenuItem>
            </MenuList>
        );
    };

    getActionMenuItem = selectedGroups => {
        const actions = this.getActions(this.props);
        const availableActions = this.getAvailableActions(
            selectedGroups,
            actions
        );

        const menuItems =
            availableActions.length > 0
                ? availableActions.map(action => ({
                      key: action.key,
                      name: action.name,
                      onClick: dropdownCloseCallback => {
                          action.action(selectedGroups);
                          dropdownCloseCallback();
                      },
                  }))
                : [
                      {
                          key: 'no',
                          name: 'No common action',
                          onClick: dropdownCloseCallback => {
                              dropdownCloseCallback();
                          },
                      },
                  ];

        return menuItems;
    };

    handleSelectionChange = val => {
        this.setState({ selectedGroups: val });
    };

    render() {
        const { editable } = this.props;
        const { groups, loading, loaded, selectedGroups } = this.state;
        const columns = this.getColumns(editable);

        const selectedActions = this.getActionMenuItem(selectedGroups);

        return (
            <>
                <WhiteBack />
                {this.state.currentAction && this.renderDialog()}
                <MTable
                    stateKey="Groups"
                    canChange={editable}
                    addMenuItems={this.getAddGroupAction(editable)}
                    columns={columns}
                    data={groups}
                    loading={loading}
                    title={null}
                    actionMenuItems={selectedActions}
                    onSelectionChange={this.handleSelectionChange}
                    options={{
                        selection: editable,
                        selectionProps: rowData => {
                            let disabled = true;
                            if (
                                rowData.population_mode === 'manual' &&
                                rowData.status === 'active' &&
                                editable &&
                                rowData.name !== 'Recovery'
                            ) {
                                disabled = !(
                                    rowData.managed_by === 'PB' ||
                                    rowData.managed !== 'managed'
                                );
                            }

                            return {
                                disabled: disabled,
                            };
                        },
                    }}
                />
                {loaded && groups.length <= 1 && (
                    <Dashboard width="70" align="center">
                        <DashboardBox>
                            <DashboardBoxContent>
                                <Grid container spacing={3} direction="row">
                                    <Grid item xs="auto">
                                        <img src={GroupsImg} alt="groups" />
                                    </Grid>
                                    <Grid item xs>
                                        <Grid
                                            container
                                            justify="center"
                                            direction="column"
                                            style={{ height: '100%' }}
                                        >
                                            <Typography
                                                variant="h5"
                                                gutterBottom
                                            >
                                                Sharing passwords and notes is
                                                easy by adding users to groups
                                            </Typography>
                                            <Typography
                                                color="textSecondary"
                                                variant="body1"
                                                gutterBottom
                                                style={{ marginBottom: 24 }}
                                            >
                                                Start by creating the first
                                                group for this company
                                            </Typography>
                                            <Typography
                                                color="textSecondary"
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                For example: Marketing, IT or
                                                Clients
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </DashboardBoxContent>
                            <Access.Change permission={Access.COMPANIES_GROUPS}>
                                <DashboardBoxActions>
                                    <Button
                                        color="secondary"
                                        onClick={() => this.handleAddGroup()}
                                    >
                                        ADD NEW GROUP
                                    </Button>
                                </DashboardBoxActions>
                            </Access.Change>
                        </DashboardBox>
                    </Dashboard>
                )}
            </>
        );
    }
}

export default withCompanyContext(Groups);
